import React, { createContext, useContext, useMemo, useState } from 'react';
import { useEffectOnce } from 'Hooks';
import { BasketService } from 'Services';
import { GO_TO } from './JourneyContext';
import * as process from 'process';
import { ConnectionType, Origin, getBreakdownStepIndex } from 'Constants';

export interface AppContextValue {
  connectionType?: ConnectionType;
  origin?: Origin;
  breadcrumb: boolean;
  eligibleToShortcutPaypal: boolean;
  basketQuantity: number;
  email: string;
  setEmail: React.Dispatch<React.SetStateAction<string>>;
  newFontDisplayed: boolean;
  kameleoonInjected: boolean;
  currentBreakdownStep: number;
}

export const AppContext = createContext<AppContextValue | undefined>(undefined);

export const useAppContext = (): AppContextValue => {
  const context = useContext(AppContext);

  if (!context) {
    throw new Error('useAppContext must be within AppContextProvider');
  }

  return context;
};

interface Props {
  children?: React.ReactNode;
}

export const AppContextProvider: React.FC<Props> = ({ children }) => {
  const [connectionType, setConnectionType] = useState<ConnectionType>();
  const [origin, setOrigin] = useState<Origin>();
  const [breadcrumb, setBreadcrumb] = useState(false);
  const [eligibleToShortcutPaypal, setEligibleToShortcutPaypal] = useState(false);
  const [newFontDisplayed, setNewFontDisplayed] = useState(false);
  const [kameleoonInjected, setKameleoonInjected] = useState(false);
  const [basketQuantity, setBasketQuantity] = useState(0);
  const [email, setEmail] = useState('');
  const [currentBreakdownStep, setCurrentBreakdownStep] = useState<number>(3);

  type displayType = 'breadcrumb' | 'eligibleToShortcutPaypal' | 'pipe';
  const shouldForceDisplay = (display: displayType) => {
    if (process.env.NODE_ENV !== 'development') return false;

    switch (display) {
      case 'breadcrumb':
        return process.env.REACT_APP_WITH_BREADCRUMB === 'true';
      case 'eligibleToShortcutPaypal':
        return process.env.REACT_APP_ELIGIBLE_TO_SHORTCUT_PAYPAL === 'true';
      case 'pipe':
        return process.env.REACT_APP_PIPE === 'true';
      default:
        return false;
    }
  };

  const buildConnectionType = (acrValues: string[]): ConnectionType =>
    acrValues.includes('darty_pro') ? ConnectionType.DPRO : ConnectionType.DCOM;

  const buildOrigin = (acrValues: string[]): Origin => {
    if (acrValues.includes('breakdown')) return Origin.BREAKDOWN;
    if (acrValues.includes('pipe_darty_max')) return Origin.PIPE_DARTY_MAX;
    if (acrValues.includes('pipe_slot_post_purchase')) return Origin.PIPE_SLOT_POST_PURCHASE;
    if (acrValues.includes('pipe_pse_post_purchase')) return Origin.PIPE_PSE_POST_PURCHASE;
    return acrValues.includes('pipe') || shouldForceDisplay('pipe') ? Origin.PIPE : Origin.CARE;
  };

  const saveConnectionType = () => {
    const url = new URL(window.location.href);
    const goTo = url.searchParams.get('goto') || localStorage.getItem(GO_TO);
    let goToUrl;
    try {
      goToUrl = new URL(goTo || '');
    } catch (error) {
      return;
    }
    if (!goToUrl) return;

    const acrValuesStr = goToUrl.searchParams.get('acr_values') || '';
    const acrValues = acrValuesStr.split(' ');
    setConnectionType(buildConnectionType(acrValues));
    setOrigin(buildOrigin(acrValues));
    setBreadcrumb(acrValues.includes('with_breadcrumb') || shouldForceDisplay('breadcrumb'));
    setEligibleToShortcutPaypal(acrValues.includes('eligible_to_shortcut_paypal') || shouldForceDisplay('eligibleToShortcutPaypal'));
    setNewFontDisplayed(acrValues.includes('with_new_font_displayed'));
    setKameleoonInjected(acrValues.includes('inject_kameleoon_script'));
    setCurrentBreakdownStep(getBreakdownStepIndex(acrValues));
  };

  useEffectOnce(() => {
    saveConnectionType();

    BasketService.getBasketQuantity().then(setBasketQuantity);
  });

  const value = useMemo(
    () => ({
      connectionType,
      origin,
      breadcrumb,
      eligibleToShortcutPaypal,
      basketQuantity,
      email,
      setEmail,
      newFontDisplayed,
      setNewFontDisplayed,
      kameleoonInjected,
      currentBreakdownStep,
    }),
    [
      connectionType,
      origin,
      breadcrumb,
      eligibleToShortcutPaypal,
      basketQuantity,
      email,
      newFontDisplayed,
      kameleoonInjected,
      currentBreakdownStep,
    ],
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};
